<template>
    <div :class="classList">
        <div v-if="isPageReady && !invitedUserEmail">
            <common-page-header
                :heading="showAcceptingInsurance ? $t('pages.profile.professional.availability.choose') : getPageHeader"
                :content="showNotAcceptingInsurance ? `${selectedUser.name} ${selectedUser.designation}` : ''"
                :isCenter="true"
                :contentSpacing="showAcceptingInsurance ? 'medium' : ''"
                size="minor"
            ></common-page-header>

            <common-page-header
                v-if="showNotAcceptingInsurance"
                :content="$t('modals.make_direct_referal_modal.content')"
                :isCenter="true"
                size="minor"
            ></common-page-header>

            <common-page-header
                v-if="showNotAcceptingInsurance"
                :content="$t('modals.make_direct_referal_modal.insurance_plans', { insurance: insurance.name })"
                :isCenter="true"
                size="minor"
            ></common-page-header>

            <common-content-spacer
                v-show="showNotAcceptingInsurance"
                class="direct-referral__no-insurance-container"
                height="medium"
            >
                <common-divider class="direct-referral__header"></common-divider>
                <p class="direct-referral__title">
                    {{ $t('modals.make_direct_referal_modal.buttons.find_provider') }}
                </p>

                <common-buttons>
                    <common-button
                        :label="$t('modals.make_direct_referal_modal.buttons.match_referral')"
                        size="large"
                        type="primary"
                        @click="matchReferral()"
                    ></common-button>
                </common-buttons>
            </common-content-spacer>

            <common-content-spacer v-if="showAcceptingInsurance" height="medium">
                <user-card
                    v-if="practiceTypeValue === PracticeTypeEnum.PHYSICIAN"
                    :physician="selectedUser"
                    size="medium"
                ></user-card>

                <practice-card
                    v-if="practiceTypeValue === PracticeTypeEnum.ANCILLARY"
                    :practice="selectedUser"
                    size="medium"
                ></practice-card>

                <div>
                    <common-divider>
                        <p class="direct-referral__check_availability_header">
                            {{ $t('modals.make_direct_referal_modal.availability') }}
                        </p>
                    </common-divider>

                    <availability-locations
                        v-if="practiceLocations.length"
                        :hoverTitle="$t('pages.profile.professional.availability.choose')"
                        :userId="selectedUser.id"
                        :maxNumberOfItems="maxNumberOfItems"
                        :locations="practiceLocations"
                        @go-to-location="eprescribe ? openTestsSelection($event) : goToSchedule($event)"
                    ></availability-locations>
                </div>
            </common-content-spacer>

            <common-content-spacer v-show="!showAcceptingInsurance && !showNotAcceptingInsurance" height="medium">
                <div class="direct-referral__inputs">
                    <common-user-search-autocomplete
                        ref="searchAC"
                        class="direct-referral__input"
                        :label="searchLabel"
                        :placeholder="searchPlaceholder"
                        :user="user"
                        :practiceType="practiceTypeValue"
                        :isDisabled="isSending"
                        @user-select="userSelected($event)"
                        @input-change="removeSearchTerm"
                        @no-results="setSearchTerm"
                    ></common-user-search-autocomplete>

                    <div class="direct-referral__insurance">
                        <label class="form-field__label">
                            {{ $t('forms.auth.practice.fields.insurance_plans.placeholder_patient') }}
                        </label>
                        <select-input
                            class="direct-referral__input"
                            :isDisabled="isSending"
                            :placeholder="$t('forms.auth.practice.fields.insurance_plans.start_typing')"
                            :options="insurancePlans"
                            :groupOptions="true"
                            :expandOptionsRight="true"
                            :isSearchable="true"
                            :value="insuranceId ?? patientPrimaryInsuranceTypeId"
                            icon="insurance"
                            @change="changeInsurance($event)"
                        ></select-input>
                    </div>
                </div>

                <div class="direct-referral__tabs">
                    <label class="form-field__label">{{ $t('forms.referrals.search.fields.search_by_time_window.label') }}</label>
                    <tabs-input
                        :options="timeSlotOptions"
                        :value="searchByTimeWindow"
                        :size="isMobileLarge ? 'extra-small' : 'small'"
                        @change="searchByTimeWindow = $event"
                    ></tabs-input>

                    <div v-if="searchByTimeWindow === 'custom'" class="direct-referral__tabs-custom">
                        <label class="form-field__label">Select Date</label>
                        <date-range-input
                            :value="customDateRange"
                            opens="left"
                            :isSingle="true"
                            @change="customDateRange = $event"
                        ></date-range-input>
                    </div>
                </div>

                <common-content-spacer v-if="isSubscriptionPremium" height="small">
                    <p class="direct-referral__invite">
                        <b>{{ selectedUser.name || `${selectedUser.firstName} ${selectedUser.lastName}` }}</b>
                        {{ $t('modals.make_direct_referal_modal.inivte_billing.default_plan') }}
                    </p>
                    <p class="direct-referral__invite">
                        {{ $t('modals.make_direct_referal_modal.inivte_billing.click') }}
                        <span @click="inviteUserToSubscribe()">
                            {{ $t('modals.make_direct_referal_modal.inivte_billing.here') }}
                        </span>
                        {{ $t('modals.make_direct_referal_modal.inivte_billing.premium') }}
                    </p>
                </common-content-spacer>

                <refer-outsider
                    v-if="searchTerm"
                    :name="searchTerm"
                    @proceed-to-referral-creation="proceedToReferralCreation"
                ></refer-outsider>

                <common-buttons v-else>
                    <common-button
                        type="primary"
                        :isProcessing="isSending"
                        :label="$t('common.microcopy.verify_insurance')"
                        :isDisabled="!insuranceId || !selectedUser"
                        size="large"
                        @click="submit"
                    ></common-button>
                </common-buttons>
            </common-content-spacer>
            <common-notification ref="commonSuccessNotification" :content="notificationMessage"></common-notification>
        </div>

        <invite-sent v-else-if="invitedUserEmail" :email="invitedUserEmail" @close="emitEvent({ name: 'close' })"></invite-sent>
    </div>
</template>

<script>
    import UsersAPI from 'api/UsersAPI'
    import ReferralsAPI from 'api/ReferralsAPI'
    import PracticesAPI from 'api/PracticesAPI'
    import Store from '~/store'
    import FormMixin from 'mixins/FormMixin'
    import dayjs from 'dayjs'
    import SelectInput from 'components/form-inputs/SelectInput'
    import TabsInput from 'components/form-inputs/TabsInput'
    import CommonUserSearchAutocomplete from 'components/page-elements/search-autocomplete/CommonUserSearchAutocomplete.vue'
    import UserCard from 'components/page-elements/referrals/UserCard'
    import PracticeCard from 'components/page-elements/practice/PracticeCard'
    import AvailabilityLocations from 'components/page-elements/profile/AvailabilityLocations'
    import ReferOutsider from 'components/page-elements/referrals/ReferOutsider'
    import InviteSent from 'components/page-elements/invites/InviteSent'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import PracticePlanEnum from '@/enums/PracticePlanEnum'
    import TimeWindowSearchCriteriaEnum from '@/enums/TimeWindowSearchCriteriaEnum'
    import ScreenSizeDetectionMixin from 'mixins/ScreenSizeDetectionMixin'
    import DateRangeInput from 'components/form-inputs/DateRangeInput'

    export default {
        mixins: [ScreenSizeDetectionMixin, FormMixin],
        components: {
            CommonUserSearchAutocomplete,
            SelectInput,
            TabsInput,
            UserCard,
            PracticeCard,
            AvailabilityLocations,
            ReferOutsider,
            InviteSent,
            DateRangeInput,
        },

        props: {
            user: { type: Object, default: null },
            insurancePlanId: { type: Number, default: null },
            existingReferral: { type: Object, required: false, default: null },
            activePracticeType: { type: String, default: PracticeTypeEnum.PHYSICIAN },
            showAsInnerModal: { type: Boolean, default: false },
            eprescribe: { type: Boolean, default: false },
            ehr: { type: Boolean, default: false },
            patientId: { type: Number, default: null, required: false },
            patientPrimaryInsuranceTypeId: { type: Number, default: null, required: false },
        },

        data() {
            return {
                insuranceId: null,
                insurance: null,
                selectedUser: null,
                showNotAcceptingInsurance: false,
                showAcceptingInsurance: false,
                practiceLocations: [],
                insurancePlans: [],
                isPageReady: false,
                searchTerm: null,
                invitedUserEmail: null,
                practiceTypeValue: PracticeTypeEnum.PHYSICIAN,
                PracticeTypeEnum: PracticeTypeEnum,
                PracticePlanEnum: PracticePlanEnum,
                maxNumberOfItems: null,
                notificationMessage: '',
                isSending: false,
                searchByTimeWindow: 'asap',
                customDateRange: { startDate: new Date(), endDate: new Date() },
            }
        },

        computed: {
            timeSlotOptions() {
                return [
                    ...TimeWindowSearchCriteriaEnum,
                    {
                        label: 'Custom',
                        value: 'custom',
                    },
                ]
            },

            practiceTypes() {
                return [PracticeTypeEnum.PHYSICIAN, PracticeTypeEnum.ANCILLARY].map((value) => ({
                    label: this.$t(`forms.auth.register.fields.practice_type.options.${value}.label`),
                    value,
                }))
            },

            showContent() {
                return this.showNotAcceptingInsurance
                    ? this.$t('modals.make_direct_referal_modal.content', {
                          user: this.selectedUser.name,
                          designation: this.selectedUser.designation,
                          insurance: this.insurance.name,
                      })
                    : ''
            },

            classList() {
                return {
                    'direct-referral': true,
                    'direct-referral__no-insurance': this.showNotAcceptingInsurance,
                }
            },

            searchLabel() {
                return this.practiceTypeValue === PracticeTypeEnum.PHYSICIAN
                    ? this.$t('modals.make_direct_referal_modal.physician')
                    : this.$t('modals.make_direct_referal_modal.ancillary')
            },

            searchPlaceholder() {
                return this.$t('modals.make_direct_referal_modal.start_typing')
            },

            getPageHeader() {
                if (this.showNotAcceptingInsurance) {
                    return ''
                }
                return this.practiceTypeValue === PracticeTypeEnum.PHYSICIAN
                    ? this.$t('modals.make_direct_referal_modal.refer_directly_physican')
                    : this.$t('modals.make_direct_referal_modal.refer_directly_ancillary')
            },
        },

        methods: {
            onTimeframeChange({ path, value }) {
                this.change({ path, value })

                if (path === 'searchByTimeWindow' && value === 'custom') {
                    this.showCustomCalendar = true
                    this.change({
                        path: 'customDate',
                        value: {
                            startDate: dayjs().startOf('day').toDate(),
                            endDate: null,
                        },
                    })
                } else if (path === 'searchByTimeWindow') {
                    this.showCustomCalendar = false
                }
            },
            submit() {
                this.isSending = true
                this.practiceTypeValue === PracticeTypeEnum.PHYSICIAN
                    ? this.checkUserAcceptInsurance()
                    : this.checkPracticeAcceptInsurance()
            },

            matchReferral() {
                if (this.eprescribe) {
                    this.$emit('match-eprescribe')
                    return
                }

                this.$router.push({
                    name: 'referrals.new',
                    query: { insurancePlanId: this.insuranceId, practiceType: this.practiceTypeValue },
                })
            },

            checkPracticeAcceptInsurance() {
                PracticesAPI.checkPracticeAcceptingInsuranceType({
                    practiceId: this.selectedUser.id,
                    insuranceTypeId: this.insuranceId,
                }).then((res) => {
                    PracticesAPI.getPracticeLocations(this.selectedUser.id).then((locations) => {
                        this.isPageReady = true

                        this.practiceLocations = locations.filter((location) => location.isAvailabilitySet)
                        this.practiceLocations.forEach((item) => delete item.practiceName)
                        this.showNotAcceptingInsurance = !res.isAccepting
                        if (this.practiceLocations.length === 1 && !this.showNotAcceptingInsurance) {
                            if (this.eprescribe) {
                                const payload = {
                                    selectedUser: this.selectedUser,
                                    insuranceId: this.insuranceId,
                                    practiceLocationId: this.practiceLocations[0].id,
                                }
                                this.$emit('open-test-selection', payload)
                                return
                            }
                            this.goToSchedule({ practiceLocationId: this.practiceLocations[0].id })
                        } else {
                            this.showAcceptingInsurance = res.isAccepting
                            this.insurance = res.insurancePlan
                            this.selectedUser = res.practice
                            this.selectedUser.specialities = []
                        }
                    })
                })
            },

            checkUserAcceptInsurance() {
                UsersAPI.checkUserAcceptingInsuranceType({
                    userId: this.selectedUser?.id,
                    insuranceTypeId: this.insuranceId,
                }).then((res) => {
                    PracticesAPI.getUserLocations(this.selectedUser.id).then((locations) => {
                        this.isPageReady = true

                        this.practiceLocations = locations.filter((location) => location.isAvailabilitySet)
                        this.showNotAcceptingInsurance = !res.isAccepting

                        if (this.practiceLocations.length === 1 && !this.showNotAcceptingInsurance) {
                            if (this.eprescribe) {
                                const payload = {
                                    selectedUser: res.user,
                                    insuranceId: this.insuranceId,
                                    practiceLocationId: this.practiceLocations[0]?.id,
                                }
                                this.$emit('add-eprescribe', payload)
                                return
                            }
                            this.goToSchedule({ practiceLocationId: this.practiceLocations[0]?.id })
                        } else {
                            this.showAcceptingInsurance = res.isAccepting
                            this.insurance = res.insurancePlan
                            this.selectedUser = res.user
                        }
                    })
                })
            },

            inviteUserToSubscribe() {
                const data = {
                    toUserId: this.selectedUser.id,
                    ...(this.practiceTypeValue !== PracticeTypeEnum.PHYSICIAN && { referringTo: PracticeTypeEnum.ANCILLARY }),
                }
                UsersAPI.inivteUserToSubscribe(data).then(() => {
                    this.removeSearchTerm()
                    this.insuranceId = null
                    this.selectedUser = null
                    this.removeSearchTerm()
                    this.$refs.searchAC.removeSelectedUser()
                    this.$refs.searchAC.searchTerm = ''
                    this.notificationMessage = this.$t('modals.make_direct_referal_modal.inivte_billing.invite_success')
                    this.$refs.commonSuccessNotification.showNotification()
                })
            },

            changeInsurance(event) {
                this.insuranceId = event
            },

            userSelected(event) {
                this.selectedUser = event
            },

            selectAgain() {
                this.showNotAcceptingInsurance = false
            },

            practiceTypeChange(event) {
                this.removeSearchTerm()
                this.insuranceId = null
                this.selectedUser = null
                this.removeSearchTerm()
                this.$refs.searchAC.removeSelectedUser()
                this.$refs.searchAC.searchTerm = ''
                this.practiceTypeValue = event
            },

            openTestsSelection({ practiceLocationId }) {
                const payload = {
                    selectedUser: this.selectedUser,
                    insuranceId: this.insuranceId,
                    practiceLocationId,
                }
                this.$emit('open-test-selection', payload)
            },

            goToSchedule({ practiceLocationId }) {
                if (this.practiceTypeValue === PracticeTypeEnum.PHYSICIAN) {
                    ReferralsAPI.getDataForDirectReferral(this.selectedUser.id).then((res) => {
                        const query = {
                            userId: res.referredBy,
                            practiceType: res.practice.practiceType,
                            searchBy: 'zipcode',
                            insurancePlanId: res.insurancePlanId || this.insuranceId,
                            specialityId: res.specialityId,
                            subSpecialityId: res.subSpecialityId,
                            zip: res.practice.zip,
                            physicianId: res.referredTo,
                            practiceId: res.practice.id,
                            practiceLocationId,
                            searchByTimeWindow: this.searchByTimeWindow,
                            startDate: this.searchByTimeWindow === 'custom' ? this.customDateRange?.startDate : undefined,
                            endDate: this.searchByTimeWindow === 'custom' ? this.customDateRange?.endDate : undefined,
                        }

                        if (this.ehr) {
                            this.$router.push({
                                name: `ehr.referrals.new.make`,
                                query: {
                                    ...query,
                                    key: this.$route.query.key,
                                    externalBooking: 'ehr',
                                },
                                params: {
                                    existingReferral: this.existingReferral,
                                },
                            })
                            return
                        }

                        this.$router.push({
                            name: `referrals.new.make`,
                            query: query,
                            params: {
                                existingReferral: this.existingReferral,
                            },
                        })
                    })
                } else {
                    const query = {
                        practiceLocationId,
                        practiceType: this.practiceTypeValue,
                        practiceId: this.selectedUser.id,
                        insurancePlanId: this.insuranceId,
                        practiceName: this.selectedUser.name,
                        searchBy: 'zipcode',
                        searchByTimeWindow: this.searchByTimeWindow,
                        startDate: this.searchByTimeWindow === 'custom' ? this.customDateRange?.startDate : undefined,
                        endDate: this.searchByTimeWindow === 'custom' ? this.customDateRange?.endDate : undefined,
                    }
                    if (this.ehr) {
                        this.$router.push({
                            name: `ehr.referrals.services.tests`,
                            query: { ...query, externalBooking: 'ehr' },
                            params: {
                                existingReferral: this.existingReferral,
                            },
                        })
                        return
                    }
                    this.$router.push({
                        name: `referrals.services.tests`,
                        query: query,
                        params: {
                            existingReferral: this.existingReferral,
                        },
                    })
                }
                this.emitEvent({ name: 'location-selected' })
            },

            removeSearchTerm() {
                this.searchTerm = null
            },

            setSearchTerm({ searchTerm }) {
                this.searchTerm = searchTerm
            },

            inviteSent({ email }) {
                this.invitedUserEmail = email
            },

            setNumberOfItems() {
                if (this.isTablet) {
                    this.maxNumberOfItems = 1
                    return
                }
                if (this.isLaptop) {
                    this.maxNumberOfItems = 2
                    return
                }

                this.maxNumberOfItems = 3
            },

            proceedToReferralCreation(payload) {
                this.externalUser = {
                    firstName: payload.firstName,
                    lastName: payload.lastName,
                    email: payload.email,
                }

                if (this.practiceTypeValue === PracticeTypeEnum.ANCILLARY) {
                    this.$router.push({
                        name: `referrals.services.tests.outsider`,
                        query: {
                            insurancePlanId: this.insuranceId,
                            practiceType: this.practiceTypeValue,
                            outsider: true,
                            ...this.externalUser,
                            patientId: this.patientId,
                        },
                    })
                    return
                }

                this.$router.push({
                    name: `referrals.new.make`,
                    query: {
                        insurancePlanId: this.insuranceId,
                        practiceType: this.practiceTypeValue,
                        outsider: true,
                        ...this.externalUser,
                        patientId: this.patientId,
                    },
                })
            },
        },

        watch: {
            windowWidth() {
                this.setNumberOfItems()
            },
        },

        mounted() {
            this.setNumberOfItems()
            if (this.activePracticeType) {
                this.practiceTypeValue = this.activePracticeType
            }
            this.insurancePlans = Store.getters['auth/getInsurancePlans']

            this.selectedUser = this.user
            this.insuranceId = this.insurancePlanId
            this.isPageReady = true

            if (this.patientInsuranceId) {
                this.insuranceId = this.patientInsuranceId
            }
        },
    }
</script>
<style scoped lang="scss">
    @import 'styles/components/referrals/_app-direct-referral';
    .timeslot-tabs {
        margin-left: 40px;
        margin-right: 40px;
    }
    .calendar-area {
        display: flex;
        flex-direction: column;
        align-items: center;
    }
</style>
